import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { calculateTimeInSeconds } from '../../utils';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';

import {
  generalInfoSummaryQuery,
  consolidatedScoresSummaryQuery,
  scoresStatsQuery
} from './api';

import PopupMessages from '../UI/introGameLayout/popupMessages';
import Summary from '../UI/game/summary';
import PerformanceRankingInfo from '../UI/performanceRankingInfo';

import './styles.css';

export default function ThePrinciplesSummaryFinal() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const isAfterFeedback = searchParams.has('after-feedback');

  const {
    getUser,
    getCurrentThePrinciplesGameId,
    saveCurrentGameId,
    isInstructor
  } = useAuth();

  const user = getUser();

  const gameId = getCurrentThePrinciplesGameId();

  const [currentGameSessionAttempt, setCurrentGameSessionAttempt] = useState(0);

  const [rank, setRank] = useState('');

  const [points, setPoints] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [averageTime, setAverageTime] = useState(0);

  const [firstAttemptPoints, setFirstAttemptPoints] = useState(0);
  const [firstAttemptAccuracy, setFirstAttemptAccuracy] = useState(0);
  const [firstAttemptAverageTime, setFirstAttemptAverageTime] = useState(0);

  const [isPerformanceRankingInfoVisible, setIsPerformanceRankingInfoVisible] = useState(false);

  const [dataLoaded, setDataLoaded] = useState(false);

  const [noButtonText, setNoButtonText] = useState('No');

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const { data: consolidatedScores } = useQuery(consolidatedScoresSummaryQuery());
  const { data: scoresStats } = useQuery(scoresStatsQuery({ gameId }));

  const gamesSessions = generalInfo?.sessions;

  const principlesScores = useMemo(() => consolidatedScores?.principles || [], [consolidatedScores]);

  const currentSessionScores = useMemo(() => {
    if (!gamesSessions || !principlesScores.length) return null;

    const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
    const currentGameSession = currentGameSessions.find(session => !session.completed)
      || currentGameSessions.filter(session => session.completed).reduce((max, s) => (s.id > (max?.id || 0) ? s : max), null);

    return principlesScores.find(s => s.game === gameId && s.game_session === currentGameSession?.id);
  }, [gamesSessions, principlesScores, gameId, user?.id]);

  const firstAttemptSessionScores = useMemo(() => {
    return principlesScores.find(s => s.game === gameId && s.attempt === 1);
  }, [principlesScores, gameId]);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSessions = gamesSessions.filter(session => session.user === user?.id && session.game === gameId);
      const currentGameSession = currentGameSessions.find(session => !session.completed);

      if (currentGameSession) {
        setCurrentGameSessionAttempt(currentGameSession?.attempt);
      } else {
        const completedGameSession = currentGameSessions
          .filter(session => session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);

        setCurrentGameSessionAttempt(completedGameSession?.attempt);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(currentSessionScores && scoresStats) {
      const summary = currentSessionScores.session_summary;
      const totalPoints = summary.total_points_session;
      const totalAccuracy = summary.total_accuracy_session;

      const levels = currentSessionScores.levels || [];
      const avgTime = levels.length > 0
        ? (levels.reduce((sum, l) => sum + calculateTimeInSeconds(l.average_time), 0) / levels.length).toFixed(2)
        : 0;

      setPoints(totalPoints);
      setAccuracy(totalAccuracy);
      setAverageTime(avgTime);

      // Rank/badge is server-computed and exposed as session.rank_session,
      // matching /the-principles/badge and /the-principles/summary.
      setRank(scoresStats.session?.rank_session || '');
      setDataLoaded(true);
    }
  }, [currentSessionScores, scoresStats]);

  useEffect(() => {
    if(firstAttemptSessionScores) {
      const summary = firstAttemptSessionScores.session_summary;
      setFirstAttemptPoints(summary.total_points_session);
      setFirstAttemptAccuracy(summary.total_accuracy_session);

      const levels = firstAttemptSessionScores.levels || [];
      const avgTime = levels.length > 0
        ? (levels.reduce((sum, l) => sum + calculateTimeInSeconds(l.average_time), 0) / levels.length).toFixed(2)
        : 0;
      setFirstAttemptAverageTime(avgTime);
    }
  }, [firstAttemptSessionScores]);

  useEffect(() => {
    if (currentGameSessionAttempt >= 3) {
      setNoButtonText('Close');
    }
  }, [currentGameSessionAttempt]);

  const handleYesButtonClick = () => {
    saveCurrentGameId(gameId, 'The Principles');
    navigate('/acc/transition');
  }

  const handleNoButtonClick = () => {
    // Instructors land on /assignments (a student-facing page) when
    // they preview a game and click "No" on summary-final. Send them
    // to their own assignments listing instead so the page they hit
    // matches their role.
    navigate(isInstructor() ? '/instructor/settings/assignments' : '/assignments');
  }

  const popupMessages = useMemo(() => {
    const initialTitle = isAfterFeedback ? '<h2 class="align-center">Thanks for the feedback!</h2>' : '<h2 class="align-center">That’s all for now!</h2>';
    const messages = [{
      name: 'Bailey',
      text: initialTitle
    }];
    let additionalText = '';

    if (currentGameSessionAttempt < 3) {
      additionalText += '<p class="align-center">Would you like to try again to improve your score?</p><br>';
    }
    if (additionalText) {
      messages[0] = {
        ...messages[0],
        text: messages[0].text + additionalText
      };
    }
    return messages;
  }, [currentGameSessionAttempt, isAfterFeedback]);

  const handleBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(true);
  }

  const handleCloseBadgeInfoButtonClick = () => {
    setIsPerformanceRankingInfoVisible(false);
  }

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        isPerformanceRankingInfoVisible &&
          <div className="background-overlay">
            <PerformanceRankingInfo position="center" onCloseButtonClick={handleCloseBadgeInfoButtonClick} />
          </div>
      }
      {
        dataLoaded &&
          <div className="game-summary-container">
            <div className="summary-popup-messages-container">
              <PopupMessages
                messages={popupMessages}
                messageSize="small"
              />
              <div className="summary-final-buttons">
                {
                  currentGameSessionAttempt < 3 &&
                    <button className="button-navi" onClick={handleYesButtonClick}>Yes</button>

                }
                <button className="button-navi" onClick={handleNoButtonClick}>{noButtonText}</button>
              </div>
            </div>
            <Summary
              rank={rank}
              points={points}
              firstAttemptPoints={firstAttemptPoints}
              lines={[
                {label: 'Accuracy', value: accuracy, firstAttemptValue: firstAttemptAccuracy},
                {label: 'Average Time', value: averageTime, firstAttemptValue: firstAttemptAverageTime},
              ]}
              onBadgeInfoButtonClick={handleBadgeInfoButtonClick}
            />
          </div>
      }
    </Layout>
  );
}
